/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable quote-props */
// eslint-disable naming-convention
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../api/base';
import {
  alertFilter,
  analyticsCommon,
  analyticsConvoy,
  analyticsFrequency,
  carFilter,
  metaVehicleMakes,
  searchCameras,
} from '../../api/paths';
import {
  ConvoyAnalyticsResponse,
  FrequencyAnalyticsResponse,
  SearchCamera,
  SearchCameraApiParams,
  SearchCameraClientFacet,
  SearchCamerasResult,
  SearchReadsResponseType,
} from '../../api/paths.types';
import { RootState } from '../store';
import axios from 'axios';
import moment from 'moment';

export type FilterBucketsType = {
  id: number;
  count: number;
  name: string;
  code?: string;
};

export type CarDataTypes = Record<string, FilterBucketsType[]>;

export type CarDataProps = {
  id: number;
  name: string;
  confidence?: number;
};

export type ReadOwningOrganization = {
  id: string;
  name: string;
  address: string;
  ori: string;
  phone_number: string;
};

export type CarResultType = {
  id: string;
  created: string;
  client: {
    id: string;
    name: string;
  };
  camera: {
    id: number;
    name: string;
  };
  center: number[];
  camera_id?: string;
  address: string;
  plate: string;
  plate_confidence: number;
  region: {
    id: number;
    name: string;
    code: string;
    confidence: number;
  };
  region_id?: string;
  region_confidenece?: number;
  vehicle_heading?: number;
  image: string;
  image_crop: string;
  night: boolean;
  v_body: CarDataProps;
  v_type: CarDataProps;
  v_make: CarDataProps;
  v_model: CarDataProps;
  // No longer returned by the reads API — replaced by v_model. Kept optional
  // for older payloads that may still include it.
  v_series?: CarDataProps;
  v_color: CarDataProps;
  location: {
    lat: number;
    lon: number;
  };
  organization?: ReadOwningOrganization;
};

export type AlertEntityType = {
  id: number;
  name: string;
  code?: string;
};

export type AlertCarResultType = {
  address: string;
  alert_type: AlertEntityType;
  camera: AlertEntityType;
  client: AlertEntityType;
  created: string;
  id: string;
  read_id: string;
  image: string;
  image_crop: string;
  hotlist: AlertEntityType;
  location: {
    lat: number;
    lon: number;
  };
  notes: string;
  plate: string;
  region: AlertEntityType;
  center: number[];
};

// Meta
export type MetaVehicleMake = {
  id: number;
  name: string;
  image: string;
};

// Convoy Insight Types
export type AnalysedConvoy = {
  count: number;
  results: CarResultType[];
  took: number;
};

export type MatchedConvoy = {
  count: number;
  results: ConvoyMatchResult[];
  took: number;
};

export type ConvoyMatchResult = {
  count: number;
  details: ConvoyMatchResultDetail;
  plate: string;
  reads: CarResultType[];
};

export type ConvoyMatchResultDetail = {
  image: string;
  image_crop: string;
  plate_confidence: number;
  region: {
    id: number;
    name: string;
    code: string;
  };
  v_body: CarDataProps;
  v_color: CarDataProps;
  v_make: CarDataProps;
  v_model: CarDataProps;
  v_series?: CarDataProps;
  v_type: CarDataProps;
};

export interface FrequencyCarType extends CarResultType {
  source: number;
  count: number;
}

export interface FetchMapDialogDataParams extends SearchCameraApiParams {
  state: 'map' | 'alert' | 'insightCommon';
}

interface MapMarkersData {
  clients: SearchCameraClientFacet[];
  cameras: SearchCamera[];
}

interface InitialData {
  mapDialogData: {
    map: MapMarkersData;
    alert: MapMarkersData;
    insightCommon: MapMarkersData;
  };
  mapCarData?: CarDataTypes | null;
  mapResults: CarResultType[];
  mapNextUrl: string | null;
  plateResults: CarResultType[];
  plateCarData: CarDataTypes | null;
  plateNextUrl: string | null;
  alertResults: AlertCarResultType[];
  alertCarData: CarDataTypes | null;
  alertNextUrl: string | null;
  mapResultCount: number | null;
  plateResultCount: number | null;
  alertResultCount: number | null;
  tableResults: CarResultType[];
  analytics: {
    convoy: {
      analysedResult: CarResultType[];
      matchedResult: ConvoyMatchResult[];
      result: CarResultType[];
      victimPlate: string | null;
    };
    frequency: {
      result: FrequencyCarType[];
      details: CarResultType[];
      totalCount: number | null;
    };
    common: {
      details: CarResultType[];
      totalCount: number | null;
      result: Result[];
      errorMessage: string | null;
    };
  };
  meta: {
    vehicleMake: MetaVehicleMake[];
    // Model facet buckets for one make, fetched without touching the search
    // results. makeId tags which make the buckets belong to so out-of-order
    // responses and stale lists can be ignored by consumers.
    makeModels: {
      makeId: string | null;
      buckets: FilterBucketsType[];
    };
  };
  loading: {
    mapLoading: 'idle' | 'pending' | 'succeeded' | 'failed';
    alertLoading: 'idle' | 'pending' | 'succeeded' | 'failed';
    plateLoading: 'idle' | 'pending' | 'succeeded' | 'failed';
    loadingMoreData: boolean;
    analytics: {
      convoyLoading: 'idle' | 'pending' | 'succeeded' | 'failed';
      frequencyLoading: 'idle' | 'pending' | 'succeeded' | 'failed';
      commonLoading: 'idle' | 'pending' | 'succeeded' | 'failed';
    };
    meta: {
      vehicleMake: 'idle' | 'pending' | 'succeeded' | 'failed';
      makeModels: 'idle' | 'pending' | 'succeeded' | 'failed';
    };
  };
  persistConfedence: any;
  error: string;
  // Bumped on every fresh search; load-more responses stamped with an older
  // generation are discarded instead of appending a stale query's page onto
  // the new result list.
  searchGen: {
    map: number;
    plate: number;
    alert: number;
  };
}

export interface FilterDataType {
  no_plate?: boolean | null;
  plate?: string | null;
  plate__wildcard?: string | null;
  // Holds numeric make ids — the Vehicle Make dropdown uses numeric option
  // values; the split() path in handleMake can also yield strings.
  v_make_id__in?: (string | number)[] | null;
  v_series_id__in?: string[] | null;
  created__gte?: string | null;
  created__lte?: string | null;
  client_id__in?: string[] | null;
  camera_id__in?: string[] | null;
  v_color_id__in?: string[] | null;
  v_body_id__in?: string[] | null;
  hotlist_id__in?: string[] | null;
  alert_type_id__in?: string[] | null;
  location__geo_bounding_box?: string[] | null;
  location__geo_polygon?: string[] | null;
  page?: number | 1;
  region_id?: number[] | null;
  region_id__exclude?: number | null;
  night?: boolean | null;
  page_size?: number;
  plate_confidence__range?: number[] | null;
  region_confidence__range?: number[] | null;
  v_body_confidence__range?: number[] | null;
  v_type_confidence__range?: number[] | null;
  v_series_confidence__range?: number[] | null;
  v_model_confidence__range?: number[] | null;
  v_color_confidence__range?: number[] | null;
  initial?: boolean | null;
  minimum_occurrences?: number | null;
  timeframe_offset?: number | null;
  plate__exclude?: string[] | null;
  case_number?: string;
  search_reason?: string;
  reason?: string;
  unique?: boolean;
}

// Common Analytics Types
export type CommonAnalyticsRequestPayload = {
  filters: CommonAnalyticsFilter[];
};

export type CommonAnalyticsFilter = {
  key?: number;
  client_id?: string;
  camera_ids?: string[];
  created__gte: string;
  created__lte: string;
  geo_bounding_box?: GeoBoundingBox;
  geo_bounding_polygon?: LatLng[];
  geo_polygon?: LatLng[];
};

export type GeoBoundingBox = {
  top_left: LatLng;
  bottom_right: LatLng;
};

export type LatLng = {
  lat: number;
  lon: number;
};

// Response
export type CommonAnalyticsResponse = {
  total: number;
  performance: Performance;
  results: Result[];
};

export type Performance = {
  request: number;
  analysis: number;
  details: number;
  total: number;
};

export type Result = {
  plate: string;
  region: {
    id: number;
    name: string;
    code: string;
  };
  matches: number;
  matched_filters: string[];
  reads: CarResultType[];
};

const initialState: InitialData = {
  mapDialogData: {
    map: {
      clients: [],
      cameras: [],
    },
    alert: {
      clients: [],
      cameras: [],
    },
    insightCommon: {
      cameras: [],
      clients: [],
    },
  },
  mapCarData: null,
  mapResults: [],
  mapNextUrl: null,
  plateCarData: null,
  plateResults: [],
  plateNextUrl: null,
  alertCarData: null,
  alertResults: [],
  alertNextUrl: null,
  mapResultCount: null,
  plateResultCount: null,
  alertResultCount: null,
  tableResults: [],
  analytics: {
    convoy: {
      analysedResult: [],
      matchedResult: [],
      result: [],
      victimPlate: null,
    },
    frequency: {
      result: [],
      details: [],
      totalCount: null,
    },
    common: {
      details: [],
      totalCount: null,
      result: [],
      errorMessage: null,
    },
  },
  meta: {
    vehicleMake: [],
    makeModels: {
      makeId: null,
      buckets: [],
    },
  },
  loading: {
    mapLoading: 'idle',
    plateLoading: 'idle',
    loadingMoreData: false,
    alertLoading: 'idle',
    analytics: {
      convoyLoading: 'idle',
      frequencyLoading: 'idle',
      commonLoading: 'idle',
    },
    meta: {
      vehicleMake: 'idle',
      makeModels: 'idle',
    },
  },
  persistConfedence: null,
  error: '',
  searchGen: {
    map: 0,
    plate: 0,
    alert: 0,
  },
};

// Cursor links come back as absolute URLs on the API host. Request just the
// path so the call always goes through the axios baseURL (direct or
// dev-proxied) — an absolute URL bypasses baseURL entirely, which breaks
// any setup where the page origin and the API host differ.
const toApiRelativeUrl = (url: string) => url.replace(/^https?:\/\/[^/]+/, '');

export const mapSearchFilters = (filterData: FilterDataType | null) => {
  return {
    ...filterData,
    region_id: null,
    alert_type_id__in: filterData?.alert_type_id__in?.join('__') || null,
    v_color_id__in: filterData?.v_color_id__in?.join('__') || null,
    camera_id__in: filterData?.camera_id__in?.join('__') || null,
    client_id__in: filterData?.client_id__in?.join('__') || null,
    v_body_id__in: filterData?.v_body_id__in?.join('__') || null,
    region_id__in: filterData?.region_id?.join('__') || null,
    v_series_id__in: filterData?.v_series_id__in?.join('__') || null,
    v_make_id__in: filterData?.v_make_id__in?.join('__') || null,
    location__geo_bounding_box:
      filterData?.location__geo_bounding_box?.join('__') || null,
    location__geo_polygon:
      filterData?.location__geo_polygon?.join('__') || null,
    plate_confidence__range:
      filterData?.plate_confidence__range?.join('__') || null,
    region_confidence__range:
      filterData?.region_confidence__range?.join('__') || null,
    v_body_confidence__range:
      filterData?.v_body_confidence__range?.join('__') || null,
    v_type_confidence__range:
      filterData?.v_type_confidence__range?.join('__') || null,
    v_model_confidence__range:
      filterData?.v_model_confidence__range?.join('__') || null,
    v_series_confidence__range:
      filterData?.v_series_confidence__range?.join('__') || null,
    v_color_confidence__range:
      filterData?.v_color_confidence__range?.join('__') || null,
    plate__exclude:
          filterData?.plate__exclude?.join('__') || null,
    minimum_occurences: null,
    timeframe_offset: null,
  };
};

export const mapSearchAction = createAsyncThunk(
  'getMapSearch',
  async (filterData: FilterDataType | null, thunkAPI) => {
    try {
      const { data } = await api.get<SearchReadsResponseType>(carFilter, {
        params: {
          ...filterData,
          ...mapSearchFilters(filterData),
        },
      });
      return { data };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

export const mapSearchLoadMore = createAsyncThunk<
  any,
  FilterDataType | null,
  { state: RootState }
>('loadMoreMapSearch', async (filterData, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const nextUrl = state.carDataReducer.mapNextUrl;
    const gen = state.carDataReducer.searchGen.map;

    // Use cursor URL if available (eliminates duplicates at source)
    if (nextUrl) {
      const { data } = await api.get<SearchReadsResponseType>(toApiRelativeUrl(nextUrl));
      return { ...data, gen };
    }

    // Fallback to page-based pagination (legacy)
    const { data } = await api.get<SearchReadsResponseType>(carFilter, {
      params: {
        ...filterData,
        region_id: null,
        v_color_id__in: filterData?.v_color_id__in?.join('__') || null,
        camera_id__in: filterData?.camera_id__in?.join('__') || null,
        client_id__in: filterData?.client_id__in?.join('__') || null,
        v_body_id__in: filterData?.v_body_id__in?.join('__') || null,
        region_id__in: filterData?.region_id?.join('__') || null,
        v_series_id__in: filterData?.v_series_id__in?.join('__') || null,
        v_make_id__in: filterData?.v_make_id__in?.join('__') || null,
        location__geo_bounding_box:
          filterData?.location__geo_bounding_box?.join('__') || null,
        location__geo_polygon:
          filterData?.location__geo_polygon?.join('__') || null,
        plate_confidence__range:
          filterData?.plate_confidence__range?.join('__') || null,
        region_confidence__range:
          filterData?.region_confidence__range?.join('__') || null,
        v_body_confidence__range:
          filterData?.v_body_confidence__range?.join('__') || null,
        v_type_confidence__range:
          filterData?.v_type_confidence__range?.join('__') || null,
        v_model_confidence__range:
          filterData?.v_model_confidence__range?.join('__') || null,
        v_series_confidence__range:
          filterData?.v_series_confidence__range?.join('__') || null,
        v_color_confidence__range:
          filterData?.v_color_confidence__range?.join('__') || null,
        plate__exclude:
          filterData?.plate__exclude?.join('__') || null,
        minimum_occurences: null,
        timeframe_offset: null,
      },
    });
    return { ...data, gen };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});
export const alertSearchLoadMore = createAsyncThunk<
  any,
  FilterDataType | null,
  { state: RootState }
>('loadMoreAlertSearch', async (filterData, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const nextUrl = state.carDataReducer.alertNextUrl;
    const gen = state.carDataReducer.searchGen.alert;

    // Use cursor URL if available (eliminates duplicates at source)
    if (nextUrl) {
      const { data } = await api.get<SearchReadsResponseType>(toApiRelativeUrl(nextUrl));
      return { ...data, gen };
    }

    // Fallback to page-based pagination (legacy)
    const { data } = await api.get<SearchReadsResponseType>(alertFilter, {
      params: {
        ...filterData,
        region_id: null,
        v_color_id__in: filterData?.v_color_id__in?.join('__') || null,
        camera_id__in: filterData?.camera_id__in?.join('__') || null,
        client_id__in: filterData?.client_id__in?.join('__') || null,
        region_id__in: filterData?.region_id?.join('__') || null,
        v_series_id__in: filterData?.v_series_id__in?.join('__') || null,
        hotlist_id__in: filterData?.hotlist_id__in?.join('__') || null,
        alert_type_id__in: filterData?.alert_type_id__in?.join('__') || null,
        location__geo_bounding_box:
          filterData?.location__geo_bounding_box?.join('__') || null,
        location__geo_polygon:
          filterData?.location__geo_polygon?.join('__') || null,
        plate__exclude:
          filterData?.plate__exclude?.join('__') || null,
        minimum_occurences: null,
        timeframe_offset: null,
      },
    });
    return { ...data, gen };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

export const alertSearch = createAsyncThunk(
  'getAlertSearch',
  async (filterData: FilterDataType | null, thunkAPI) => {
    try {
      const { data } = await api.get<SearchReadsResponseType>(alertFilter, {
        params: {
          ...filterData,
          region_id: null,
          v_color_id__in: filterData?.v_color_id__in?.join('__') || null,
          camera_id__in: filterData?.camera_id__in?.join('__') || null,
          client_id__in: filterData?.client_id__in?.join('__') || null,
          region_id__in: filterData?.region_id?.join('__') || null,
          v_series_id__in: filterData?.v_series_id__in?.join('__') || null,
          hotlist_id__in: filterData?.hotlist_id__in?.join('__') || null,
          alert_type_id__in: filterData?.alert_type_id__in?.join('__') || null,
          location__geo_bounding_box:
            filterData?.location__geo_bounding_box?.join('__') || null,
          location__geo_polygon:
            filterData?.location__geo_polygon?.join('__') || null,
          plate_confidence__range:
            filterData?.plate_confidence__range?.join('__') || null,
          region_confidence__range:
            filterData?.region_confidence__range?.join('__') || null,
          v_body_confidence__range:
            filterData?.v_body_confidence__range?.join('__') || null,
          v_type_confidence__range:
            filterData?.v_type_confidence__range?.join('__') || null,
          v_model_confidence__range:
            filterData?.v_model_confidence__range?.join('__') || null,
          v_series_confidence__range:
            filterData?.v_series_confidence__range?.join('__') || null,
          v_color_confidence__range:
            filterData?.v_color_confidence__range?.join('__') || null,
          plate__exclude:
            filterData?.plate__exclude?.join('__') || null,
          minimum_occurences: null,
          timeframe_offset: null,
        },
      });
      return { data };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

export const plateSearch = createAsyncThunk(
  'getPlateSearch',
  async (filterData: FilterDataType | null, thunkAPI) => {
    try {
      const { data } = await api.get<SearchReadsResponseType>(carFilter, {
        params: {
          ...filterData,
          region_id: null,
          v_color_id__in: filterData?.v_color_id__in?.join('__') || null,
          camera_id__in: filterData?.camera_id__in?.join('__') || null,
          client_id__in: filterData?.client_id__in?.join('__') || null,
          v_body_id__in: filterData?.v_body_id__in?.join('__') || null,
          region_id__in: filterData?.region_id?.join('__') || null,
          v_series_id__in: filterData?.v_series_id__in?.join('__') || null,
          v_make_id__in: filterData?.v_make_id__in?.join('__') || null,
          location__geo_bounding_box:
            filterData?.location__geo_bounding_box?.join('__') || null,
          location__geo_polygon:
            filterData?.location__geo_polygon?.join('__') || null,
          plate__exclude:
          filterData?.plate__exclude?.join('__') || null,
          minimum_occurences: null,
          timeframe_offset: null,
        },
      });
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

export const plateSearchLoadMore = createAsyncThunk<
  any,
  FilterDataType | null,
  { state: RootState }
>('loadMorePlateSearch', async (filterData, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const nextUrl = state.carDataReducer.plateNextUrl;
    const gen = state.carDataReducer.searchGen.plate;

    // Use cursor URL if available (eliminates duplicates at source)
    if (nextUrl) {
      const { data } = await api.get<SearchReadsResponseType>(toApiRelativeUrl(nextUrl));
      return { ...data, gen };
    }

    // Fallback to page-based pagination (legacy)
    const { data } = await api.get<SearchReadsResponseType>(carFilter, {
      params: {
        ...filterData,
        region_id: null,
        v_color_id__in: filterData?.v_color_id__in?.join('__') || null,
        camera_id__in: filterData?.camera_id__in?.join('__') || null,
        client_id__in: filterData?.client_id__in?.join('__') || null,
        v_body_id__in: filterData?.v_body_id__in?.join('__') || null,
        region_id__in: filterData?.region_id?.join('__') || null,
        v_series_id__in: filterData?.v_series_id__in?.join('__') || null,
        v_make_id__in: filterData?.v_make_id__in?.join('__') || null,
        location__geo_bounding_box:
          filterData?.location__geo_bounding_box?.join('__') || null,
        location__geo_polygon:
          filterData?.location__geo_polygon?.join('__') || null,
        plate__exclude:
          filterData?.plate__exclude?.join('__') || null,
        minimum_occurences: null,
        timeframe_offset: null,
      },
    });
    return { ...data, gen };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

export const analyticsConvoyAction = createAsyncThunk<
  any,
  FilterDataType | null,
  { state: RootState }
>('convoyAnalytics', async (filterData: FilterDataType | null, thunkAPI) => {
  try {
    const { data } = await api.get<ConvoyAnalyticsResponse>(analyticsConvoy, {
      params: {
        ...filterData,
        region_id: filterData?.region_id?.pop() || null,
        location__geo_bounding_box:
          filterData?.location__geo_bounding_box?.join('__') || null,
        location__geo_polygon:
          filterData?.location__geo_polygon?.join('__') || null,
        minimum_occurrences: filterData?.minimum_occurrences || null,
        timeframe_offset: filterData?.timeframe_offset || null,
        page: null,
      },
    });
    return { data, victimPlate: filterData?.plate };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

export const analyticsFrequencyAction = createAsyncThunk<
  any,
  FilterDataType | null,
  { state: RootState }
>('frequencyAnalytics', async (filterData: FilterDataType | null, thunkAPI) => {
  try {
    const { data } = await api.get<FrequencyAnalyticsResponse>(
      analyticsFrequency,
      {
        params: {
          ...filterData,
          client_id__in: filterData?.client_id__in?.join('__') || null,
          camera_id__in: filterData?.camera_id__in?.join('__') || null,
          location__geo_bounding_box:
            filterData?.location__geo_bounding_box?.join('__') || null,
          location__geo_polygon:
            filterData?.location__geo_polygon?.join('__') || null,
          minimum_occurrences: filterData?.minimum_occurrences || null,
          timeframe_offset: filterData?.timeframe_offset || null,
          page: null,
        },
      },
    );
    return { data };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

export const analyticsCommonAction = createAsyncThunk<
  any,
  CommonAnalyticsFilter[],
  { state: RootState }
>('commonAnalysis', async (filterData: CommonAnalyticsFilter[], thunkAPI) => {
  try {
    const payload: CommonAnalyticsRequestPayload = {
      filters: filterData.map((filter) => ({
        created__gte: filter.created__gte,
        created__lte: filter.created__lte,
        camera_ids: filter.camera_ids,
        client_id: filter.client_id,
        geo_bounding_box: filter.geo_bounding_box,
        geo_polygon: filter.geo_polygon,
      })),
    };
    const { data } = await api.post<CommonAnalyticsResponse>(
      analyticsCommon,
      payload,
    );
    return { data };
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.detail;
        return thunkAPI.rejectWithValue({
          error: {
            ui: true,
            message: errorMessage,
          },
        });
      }
    }
    return thunkAPI.rejectWithValue({
      error: {
        ui: false,
        message: error.message,
      },
    });
  }
});

export const getMapDialogData = createAsyncThunk<
  any,
  FetchMapDialogDataParams,
  { state: RootState }
>('mapDialogData', async (params: FetchMapDialogDataParams, thunkAPI) => {
  try {
    // Exclude 'state' from params when sending to API
    const { state, ...apiParams } = params;
    const { data } = await api.get<SearchCamerasResult>(searchCameras, {
      cache: true,
      params: apiParams,
    });
    return { state, data };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      state: params.state,
      error: error.message,
    });
  }
});

export const getMetaVehicleMake = createAsyncThunk<
  any,
  void,
  { state: RootState }
>('metaVehicleMake', async (_, thunkAPI) => {
  try {
    const { data } = await api.get<MetaVehicleMake[]>(metaVehicleMakes);
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

// Fetch the model (v_series) facet buckets for one make WITHOUT writing the
// search results keys — selecting a make in a filter UI must never replace
// what the user sees in the result list.
export const getModelFacets = createAsyncThunk<
  any,
  { makeId: string },
  { state: RootState }
>('metaModelFacets', async ({ makeId }, thunkAPI) => {
  try {
    const { data } = await api.get<SearchReadsResponseType>(carFilter, {
      params: {
        created__lte: moment().utc().utcOffset(0).format(),
        created__gte: moment.utc().utcOffset(0).subtract(1, 'year').format(),
        page: 1,
        page_size: 1,
        v_make_id__in: makeId,
      },
    });
    return { makeId, buckets: data?.facets?.v_series_id ?? [] };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({ error: error.message });
  }
});

export const allCarDataSlice = createSlice({
  name: 'car-data',
  initialState,
  reducers: {
    allCarData: (state) => {
      state.mapCarData = initialState.mapCarData;
      state.mapResults = initialState.mapResults;
      state.plateResults = initialState.plateResults;
      state.plateCarData = initialState.plateCarData;
      state.plateResultCount = null;
      state.mapResultCount = null;
      state.alertResultCount = null;
      state.loading = initialState.loading;
      state.error = initialState.error;
      state.alertResults = initialState.alertResults;
      state.alertCarData = initialState.alertCarData;
    },
    resetMapData: (state) => {
      state.mapCarData = initialState.mapCarData;
      state.mapResults = initialState.mapResults;
      state.mapResultCount = null;
      state.loading.mapLoading = initialState.loading.mapLoading;
      state.error = initialState.error;
    },
    resetState: (state) => {
      state.plateResults = [];
      // state.plateCarData = null;
      state.tableResults = [];
      state.plateResultCount = null;
      // Analytics (convoy/frequency/common) are intentionally NOT cleared
      // here. They belong to the Insights tab and should survive tab
      // switches so users can return to their in-progress analysis. Use
      // resetAnalytics() for explicit analytics clearing (invoked by the
      // Dashboard-icon full reset in resetDashboardFilters).
    },
    resetAnalytics: (state) => {
      state.analytics.convoy = initialState.analytics.convoy;
      state.analytics.frequency = initialState.analytics.frequency;
      state.analytics.common = initialState.analytics.common;
      state.loading.analytics.convoyLoading = 'idle';
      state.loading.analytics.frequencyLoading = 'idle';
      state.loading.analytics.commonLoading = 'idle';
    },
    persistConfedence: (state, action) => {
      state.persistConfedence = action.payload;
    },
    setConvoyResults: (state, action) => {
      state.analytics.convoy.result = [...action.payload];
    },
    setFrequencyDetails: (state, action) => {
      state.analytics.frequency.totalCount = action.payload.count;
      state.analytics.frequency.details = [...action.payload.data];
    },
    setSelectedCommonAnalysis: (state, action) => {
      state.analytics.common.details = [...action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getMapDialogData.fulfilled,
      (
        state,
        action: PayloadAction<
          { data: SearchCamerasResult } & Pick<
            FetchMapDialogDataParams,
            'state'
          >
        >,
      ) => {
        const { state: index, data } = action.payload;
        state.mapDialogData[index].clients = [...data.facets.client_id];
        state.mapDialogData[index].cameras = [...data.results];
      },
    );
    builder.addCase(getMapDialogData.rejected, (state, action: any) => {
      const { state: index } = action.payload;
      state.mapDialogData[index].clients = [];
      state.mapDialogData[index].cameras = [];
    });
    builder.addCase(mapSearchAction.pending, (state) => {
      state.loading.mapLoading = 'pending';
      // Invalidate in-flight load-mores: bump the generation and drop the
      // old cursor so a stale page can't append onto the new results.
      state.searchGen.map += 1;
      state.mapNextUrl = null;
    });
    builder.addCase(mapSearchAction.fulfilled, (state, action: any) => {
      state.loading.mapLoading = 'succeeded';
      const { results, facets, count, next } = action.payload.data;
      state.mapResults = results;
      state.mapCarData = facets;
      state.mapResultCount = count;
      state.mapNextUrl = next;
    });
    builder.addCase(mapSearchAction.rejected, (state, action: any) => {
      state.loading.mapLoading = 'failed';
      state.error = action.payload.error;
    });
    builder.addCase(alertSearch.pending, (state) => {
      state.loading.alertLoading = 'pending';
      state.searchGen.alert += 1;
      state.alertNextUrl = null;
    });
    builder.addCase(alertSearch.fulfilled, (state, action: any) => {
      state.loading.alertLoading = 'succeeded';
      const { results, facets, count, next } = action.payload.data;
      state.alertResults = results;
      state.alertCarData = facets;
      state.alertResultCount = count;
      state.alertNextUrl = next;
    });
    builder.addCase(alertSearch.rejected, (state, action: any) => {
      state.loading.alertLoading = 'succeeded';
      state.error = action.payload.error;
    });
    builder.addCase(plateSearch.pending, (state) => {
      state.loading.plateLoading = 'pending';
      state.searchGen.plate += 1;
      state.plateNextUrl = null;
    });
    builder.addCase(plateSearch.fulfilled, (state, action: any) => {
      state.loading.plateLoading = 'succeeded';
      const { results, facets, count, next } = action.payload;
      state.plateResults = results;
      state.plateCarData = facets;
      state.tableResults = results;
      state.plateResultCount = count;
      state.plateNextUrl = next;
    });
    builder.addCase(plateSearch.rejected, (state, action: any) => {
      state.loading.plateLoading = 'failed';
      state.error = action.payload.error;
    });
    builder.addCase(mapSearchLoadMore.pending, (state) => {
      state.loading.loadingMoreData = true;
    });
    builder.addCase(mapSearchLoadMore.fulfilled, (state, action: any) => {
      state.loading.loadingMoreData = false;
      const { results, facets, count, next, gen } = action.payload;

      // A fresh search superseded this page while it was in flight.
      if (gen !== state.searchGen.map) return;

      // Cursor pagination eliminates duplicates at source - no dedup needed
      state.mapResults = [...state.mapResults, ...results];
      state.mapCarData = facets;
      state.mapResultCount = count;
      state.mapNextUrl = next;
    });
    builder.addCase(mapSearchLoadMore.rejected, (state, action: any) => {
      state.loading.loadingMoreData = false;
      state.error = action.payload.error;
    });
    builder.addCase(alertSearchLoadMore.pending, (state) => {
      state.loading.loadingMoreData = true;
    });
    builder.addCase(alertSearchLoadMore.fulfilled, (state, action: any) => {
      state.loading.loadingMoreData = false;
      const { results, facets, count, next, gen } = action.payload;

      // A fresh search superseded this page while it was in flight.
      if (gen !== state.searchGen.alert) return;

      // Cursor pagination eliminates duplicates at source - no dedup needed
      state.alertResults = [...state.alertResults, ...results];
      state.alertCarData = facets;
      state.alertResultCount = count;
      state.alertNextUrl = next;
    });
    builder.addCase(alertSearchLoadMore.rejected, (state, action: any) => {
      state.loading.loadingMoreData = false;
      state.error = action.payload.error;
    });
    builder.addCase(plateSearchLoadMore.pending, (state) => {
      state.loading.loadingMoreData = true;
    });
    builder.addCase(plateSearchLoadMore.fulfilled, (state, action: any) => {
      state.loading.loadingMoreData = false;
      const { results, facets, count, next, gen } = action.payload;

      // A fresh search superseded this page while it was in flight.
      if (gen !== state.searchGen.plate) return;

      // Cursor pagination eliminates duplicates at source - no dedup needed
      state.plateResults = [...state.plateResults, ...results];
      state.tableResults = results;
      state.plateCarData = facets;
      state.plateResultCount = count;
      state.plateNextUrl = next;
    });
    builder.addCase(plateSearchLoadMore.rejected, (state, action: any) => {
      state.loading.loadingMoreData = false;
      state.error = action.payload.error;
    });
    builder.addCase(analyticsConvoyAction.pending, (state) => {
      state.loading.analytics.convoyLoading = 'pending';
    });
    builder.addCase(analyticsConvoyAction.fulfilled, (state, action: any) => {
      state.loading.analytics.convoyLoading = 'succeeded';
      const { data, victimPlate } = action.payload;
      const { analysed, matched } = data;
      state.analytics.convoy.result = [];
      state.analytics.convoy.analysedResult = [...analysed.results];
      state.analytics.convoy.matchedResult = [...matched.results];
      state.analytics.convoy.victimPlate = victimPlate;
    });
    builder.addCase(analyticsConvoyAction.rejected, (state, action: any) => {
      state.loading.analytics.convoyLoading = 'succeeded';
      state.error = action.payload.error;
    });
    builder.addCase(analyticsFrequencyAction.pending, (state) => {
      state.loading.analytics.frequencyLoading = 'pending';
    });
    builder.addCase(
      analyticsFrequencyAction.fulfilled,
      (state, action: any) => {
        const { data } = action.payload;
        state.loading.analytics.frequencyLoading = 'succeeded';
        state.analytics.frequency.result = [...data.results];
      },
    );
    builder.addCase(analyticsFrequencyAction.rejected, (state, action: any) => {
      state.loading.analytics.frequencyLoading = 'succeeded';
      state.error = action.payload.error;
    });
    builder.addCase(analyticsCommonAction.pending, (state) => {
      state.loading.analytics.commonLoading = 'pending';
      state.analytics.common.result = [];
      state.analytics.common.totalCount = null;
      state.analytics.common.errorMessage = null;
    });
    builder.addCase(
      analyticsCommonAction.fulfilled,
      (state, action: any) => {
        const { data } = action.payload;
        state.loading.analytics.commonLoading = 'succeeded';
        state.analytics.common.result = [...data.results];
        state.analytics.common.totalCount = data.total;
      },
    );
    builder.addCase(analyticsCommonAction.rejected, (state, action: any) => {
      state.loading.analytics.commonLoading = 'succeeded';
      state.analytics.common.errorMessage = null;
      if (action.payload.error.ui) {
        state.analytics.common.errorMessage = action.payload.error.message;
      } else {
        state.error = action.payload.error;
      }
    });
    builder.addCase(
      getMetaVehicleMake.pending,
      (state) => {
        state.loading.meta.vehicleMake = 'pending';
      },
    );
    builder.addCase(
      getMetaVehicleMake.fulfilled,
      (state, action: any) => {
        const makesList = action.payload;
        state.loading.meta.vehicleMake = 'succeeded';
        state.meta.vehicleMake = [...makesList];
      },
    );
    builder.addCase(
      getMetaVehicleMake.rejected,
      (state, action: any) => {
        state.loading.meta.vehicleMake = 'failed';
        state.error = action.payload.error;
      },
    );
    builder.addCase(getModelFacets.pending, (state, action) => {
      state.loading.meta.makeModels = 'pending';
      // Clear immediately and tag with the requested make so a stale list
      // never shows under a newly selected make.
      state.meta.makeModels = {
        makeId: action.meta.arg.makeId,
        buckets: [],
      };
    });
    builder.addCase(getModelFacets.fulfilled, (state, action: any) => {
      const { makeId, buckets } = action.payload;
      // Ignore out-of-order responses for a make that is no longer current.
      if (state.meta.makeModels.makeId !== makeId) return;
      state.loading.meta.makeModels = 'succeeded';
      state.meta.makeModels = { makeId, buckets: [...buckets] };
    });
    builder.addCase(getModelFacets.rejected, (state, action: any) => {
      state.loading.meta.makeModels = 'failed';
      state.error = action.payload.error;
    });
  },
});

export default allCarDataSlice.reducer;
export const {
  allCarData,
  resetState,
  resetAnalytics,
  persistConfedence,
  setConvoyResults,
  setFrequencyDetails,
  resetMapData,
  setSelectedCommonAnalysis,
} = allCarDataSlice.actions;
